import React, { FC, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'react-router5';

import { useTranslator, useUserInfo, useVisibleDuration } from '$hooks';
import { followersActions, followersSelector } from '$store/followers';
import { CHAT, PROFILE, RESOLVE } from '$shared/constants/pages';
import { classNames } from '$utils';
import { UnitName, UnitPhoto, Verified } from '$shared/components';
import { Button } from '$uikit';
import { FeaturedProfileProps } from './FeaturedProfile.interface';
import { profileActions } from '$store/profile';

export const FeaturedProfile: FC<FeaturedProfileProps> = (props) => {
  const { cover, userId } = props;
  const t = useTranslator();
  const user = useUserInfo(userId);
  const postRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const { following } = useSelector(followersSelector);
  const isFollowed = following[userId];
  const router = useRouter();

  useVisibleDuration(postRef, () => {
    dispatch(profileActions.sendProfileView(userId));
  });

  useEffect(() => {
    return () => {
      dispatch(profileActions.sendProfileView(userId));
    };
  }, [userId]);

  const handleFollow = useCallback(
    (e) => {
      e.stopPropagation();
      dispatch(followersActions.follow(userId));
    },
    [userId],
  );

  const handleChat = useCallback(
    (e) => {
      e.stopPropagation();
      router.navigate(CHAT, { peerId: userId });
    },
    [userId],
  );

  const handleClick = useCallback(() => {
    if (user.domain) {
      router.navigate(RESOLVE, { domain: user.domain });
    } else {
      router.navigate(PROFILE, { id: user.id });
    }
  }, [user]);

  return (
    <div className="PromoProfile__cover" ref={postRef} onClick={handleClick}>
      <div
        className={classNames('PromoProfile__cover_img', { fake: !cover })}
        style={{ backgroundImage: `url(${cover || user.photo})` }}
      />

      <div className="PromoProfile__preview__info">
        <UnitPhoto onlineIndicator unit={user} size="medium" routeName={null} />

        <div className="PromoProfile__preview__info__text">
          <div className="PromoProfile__preview__info__name_wrap">
            <h1 className="PromoProfile__preview__info__name">
              <UnitName unit={user} />
            </h1>
            <Verified objectId={user.id} large />
          </div>
          <div className="PromoProfile__preview__info__subtitle">
            {user.followers} followers
          </div>
        </div>
        <div className="PromoProfile__preview__follow">
          {isFollowed ? (
            <Button size="small" onClick={handleChat} mode="secondary">
              {t('promo_post_message')}
            </Button>
          ) : (
            <Button size="small" onClick={handleFollow}>
              {t('promo_post_follow')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
